import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

interface PriceMatch {
  originalText: string;
  placeholder: string;
  tier: number;
  billingPeriod: string;
  startIndex: number;
  endIndex: number;
}

interface PriceAnalysisDisplayProps {
  originalHtml: string;
  modifiedHtml: string;
  priceMatches: PriceMatch[];
  errors: string[];
  warnings: string[];
  suggestions: string[];
  onConfirm: () => void;
  onBack: () => void;
  css?: string;
  js?: string;
}

const TIER_COLORS = [
  'bg-blue-100 text-blue-800 border-blue-200',
  'bg-green-100 text-green-800 border-green-200',
  'bg-purple-100 text-purple-800 border-purple-200',
  'bg-orange-100 text-orange-800 border-orange-200',
  'bg-red-100 text-red-800 border-red-200',
];

const PERIOD_COLORS = [
  'bg-yellow-100 text-yellow-800 border-yellow-200',
  'bg-pink-100 text-pink-800 border-pink-200',
  'bg-indigo-100 text-indigo-800 border-indigo-200',
  'bg-teal-100 text-teal-800 border-teal-200',
];

export const PriceAnalysisDisplay: React.FC<PriceAnalysisDisplayProps> = ({
  originalHtml,
  modifiedHtml,
  priceMatches = [],
  errors = [],
  warnings = [],
  suggestions = [],
  onConfirm,
  onBack,
  css = '',
  js = ''
}) => {
  const [showOriginal, setShowOriginal] = useState(false);

  // Create preview HTML like the upload page
  const generatePreviewHtml = (html: string) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>${css}</style>
      </head>
      <body>
        ${html}
        <script>${js}</script>
      </body>
      </html>
    `;
  };

  // Use modified HTML if placeholders were found, otherwise use original
  const displayHtml = priceMatches.length > 0 ? modifiedHtml : originalHtml;
  const previewHtml = generatePreviewHtml(displayHtml);

  const getTierColor = (tier: number) => {
    return TIER_COLORS[(tier - 1) % TIER_COLORS.length];
  };

  const getPeriodColor = (period: string) => {
    const periodIndex = ['monthly', 'quarterly', 'annually', 'lifetime'].indexOf(period);
    return PERIOD_COLORS[periodIndex % PERIOD_COLORS.length];
  };

  const renderHighlightedHtml = (html: string) => {
    let highlightedHtml = html;
    
    // Replace placeholders with colored badges
    priceMatches.forEach(match => {
      const tierColor = getTierColor(match.tier);
      const periodColor = getPeriodColor(match.billingPeriod);
      
      const badge = `
        <span class="inline-block px-2 py-1 mx-1 rounded text-xs font-medium border ${tierColor}">
          ${match.placeholder}
        </span>
      `;
      
      highlightedHtml = highlightedHtml.replace(match.placeholder, badge);
    });

    return highlightedHtml;
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>Price Analysis Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{priceMatches.length}</div>
              <div className="text-sm text-green-700">Prices Found</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{warnings.length}</div>
              <div className="text-sm text-yellow-700">Warnings</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{errors.length}</div>
              <div className="text-sm text-red-700">Errors</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Price Matches */}
      {priceMatches.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Detected Price Replacements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {priceMatches.map((match, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Badge className={getTierColor(match.tier)}>
                      Tier {match.tier}
                    </Badge>
                    <Badge className={getPeriodColor(match.billingPeriod)}>
                      {match.billingPeriod}
                    </Badge>
                    <span className="text-gray-600">→</span>
                    <code className="px-2 py-1 bg-gray-100 rounded text-sm">
                      {match.placeholder}
                    </code>
                  </div>
                  <div className="text-sm text-gray-500">
                    Original: <span className="font-mono">{match.originalText}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-yellow-800">Warnings</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {warnings.map((warning, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-yellow-600 mt-1">⚠️</span>
                  <span className="text-yellow-700">{warning}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800">Errors</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {errors.map((error, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-red-600 mt-1">❌</span>
                  <span className="text-red-700">{error}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-800">Suggestions</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {suggestions.map((suggestion, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-blue-600 mt-1">💡</span>
                  <span className="text-blue-700">{suggestion}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Preview Section */}
      <Card className='border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'>
        <CardHeader>
          <CardTitle className='text-green-800 dark:text-green-200'>
            {priceMatches.length > 0 ? 'Preview with Placeholders' : 'Preview (No Placeholders Found)'}
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <p className='text-green-700 dark:text-green-300'>
            {priceMatches.length > 0 
              ? `Found ${priceMatches.length} prices to replace with placeholders. Preview shows the modified pricing table.`
              : 'No prices were found to replace. Preview shows the original pricing table.'
            }
          </p>
          
          <div className='mt-6'>
            <h4 className='font-medium text-gray-900 dark:text-white mb-3'>
              Preview:
            </h4>
            <div className='border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900'>
              <iframe
                srcDoc={previewHtml}
                className='w-full h-96 border-0'
                title='Pricing Table Preview with Placeholders'
              />
            </div>
            <div className='mt-4 flex space-x-3'>
              <Button 
                variant='outline'
                onClick={() => setShowOriginal(!showOriginal)}
              >
                {showOriginal ? 'Show with Placeholders' : 'Show Original'}
              </Button>
              <Button 
                variant='outline'
                onClick={() => {
                  const newPreviewHtml = generatePreviewHtml(showOriginal ? originalHtml : displayHtml);
                  // Force iframe refresh by updating srcdoc
                  const iframe = document.querySelector('iframe[title="Pricing Table Preview with Placeholders"]') as HTMLIFrameElement;
                  if (iframe) {
                    iframe.srcdoc = newPreviewHtml;
                  }
                }}
              >
                Refresh Preview
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back to Pricing
        </Button>
        <Button 
          onClick={onConfirm}
          disabled={errors.length > 0}
          className="bg-green-600 hover:bg-green-700"
        >
          {errors.length > 0 ? 'Fix Errors First' : 'Confirm Placeholders'}
        </Button>
      </div>
    </div>
  );
};
